import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { getReporteDownload } from '../../api';
import type { ReporteListItem, ResumenMes, DesgloseReglas } from '../../api';
import { Loader, ReglasConciliacionModal } from '../../components';
import { cleanEmpresa, estadoLabel, formatMXN, pctColor } from '../../utils/format';
import { useEmpresa, EMPRESA_TODAS, type EmpresaOption } from '../../empresa';

const { Title, Paragraph, Text } = Typography;

interface RfcGroup {
  rfc:             string;
  nombre_empresa?: string;
  runs:            ReporteListItem[]; // del API ya vienen DESC por fecha
  latest?:         ReporteListItem;
}

// Misma base que Dashboard: las empresas salen del roster de EmpresaProvider,
// no solo de conciliaciones. Un cliente sin reportes se muestra igual para que
// "Empresas Registradas" refleje los clientes cargados.
function buildGroups(reportes: ReporteListItem[], empresas: EmpresaOption[], nombresClientes: Record<string, string>): RfcGroup[] {
  const byRfc = new Map<string, RfcGroup>();
  for (const r of reportes) {
    const key = r.rfc.toUpperCase();
    let g = byRfc.get(key);
    if (!g) { g = { rfc: r.rfc, runs: [] }; byRfc.set(key, g); }
    if (!g.nombre_empresa && r.nombre_empresa) g.nombre_empresa = r.nombre_empresa;
    g.runs.push(r);
  }
  for (const g of byRfc.values()) g.latest = g.runs[0];

  const sobrantes: RfcGroup[] = [];
  for (const g of byRfc.values()) {
    if (!empresas.some(e => e.value.toUpperCase() === g.rfc.toUpperCase())) sobrantes.push(g);
  }

  const result: RfcGroup[] = [];
  for (const e of empresas) result.push(byRfc.get(e.value.toUpperCase()) ?? { rfc: e.value, runs: [] });
  result.push(...sobrantes);

  return result.map(g => ({
    ...g,
    nombre_empresa: nombresClientes[g.rfc.toUpperCase()] || g.nombre_empresa,
  }));
}

export function ReportesList() {
  const { message, notification } = App.useApp();
  const navigate = useNavigate();
  const { rfc, reportes, empresas, nombresClientes, loading, error, refresh } = useEmpresa();
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [modalReglas, setModalReglas] = useState<{
    visible: boolean;
    rfc: string;
    periodo?: string;
    porcentaje: number;
    desglose?: DesgloseReglas;
  }>({ visible: false, rfc: '', porcentaje: 0 });

  // Acotado a la empresa activa en el selector global (Header) cuando hay
  // una seleccionada — antes esta pantalla siempre mostraba todas.
  const empresasAlcance = useMemo(
    () => (rfc === EMPRESA_TODAS ? empresas : empresas.filter(e => e.value === rfc)),
    [empresas, rfc],
  );
  const reportesAlcance = useMemo(
    () => (rfc === EMPRESA_TODAS ? reportes : reportes.filter(r => r.rfc === rfc)),
    [reportes, rfc],
  );

  const groups = useMemo(
    () => buildGroups(reportesAlcance, empresasAlcance, nombresClientes),
    [reportesAlcance, empresasAlcance, nombresClientes],
  );

  const stats = useMemo(() => {
    const empresasActivas = groups.length;
    const empresasAlDia   = groups.filter(g => (g.latest?.resumen?.porcentaje_conciliado ?? 0) >= 80).length;
    const facturas        = groups.reduce(
      (s, g) => s + (g.latest?.resumen?.matched ?? 0) + (g.latest?.resumen?.matched_ia ?? 0),
      0,
    );
    return { empresasActivas, empresasAlDia, facturas };
  }, [groups]);

  async function descargarExcel(rfc: string) {
    setDownloading(rfc);
    try {
      const { url } = await getReporteDownload(rfc);
      // No window.open() directo acá: incluso este await breve alcanza para
      // que algunos navegadores (Safari en particular) traten el open como
      // popup no solicitado y lo bloqueen en silencio — mismo motivo que en
      // CfdiCatalogo.tsx/ReporteDetalle.tsx. El botón de la notificación
      // hace el open dentro de un click real.
      if (url) {
        notification.success({
          message: 'Excel listo',
          description: 'El reporte de conciliación ya se generó.',
          btn: <Button type="primary" size="small" onClick={() => window.open(url, '_blank')}>Descargar</Button>,
          duration: 0,
        });
      }
    } catch {
      message.error('No se Pudo Descargar el Excel.');
    } finally {
      setDownloading(null);
    }
  }

  const columns: ColumnsType<RfcGroup> = [
    {
      title: 'RFC',
      key: 'rfc',
      width: 160,
      align: 'center',
      render: (_, g) => <div style={{ textAlign: 'left' }}><Tag style={{ fontFamily: 'monospace' }}>{g.rfc}</Tag></div>,
    },
    {
      title: 'Empresa',
      key: 'empresa',
      align: 'center',
      render: (_, g) =>
        <div style={{ textAlign: 'left' }}>
          {g.nombre_empresa
            ? <Text style={{ fontSize: 12 }}>{cleanEmpresa(g.nombre_empresa)}</Text>
            : <Text type="secondary">—</Text>}
        </div>,
    },
    {
      title: 'Conciliación',
      key: 'pct',
      width: 200,
      align: 'center',
      render: (_, g) => {
        if (!g.latest) {
          return <Text type="secondary" style={{ fontSize: 12 }}>Sin conciliaciones</Text>;
        }
        const pct = g.latest.resumen.porcentaje_conciliado || 0;
        const periodoParts = [g.latest.periodo?.inicio, g.latest.periodo?.fin].filter(Boolean);
        const periodoStr = periodoParts.length ? periodoParts.join(' — ') : undefined;
        return (
          <div
            style={{ cursor: 'pointer' }}
            title="Ver reglas de conciliación"
            onClick={() =>
              setModalReglas({
                visible: true,
                rfc: g.rfc,
                periodo: periodoStr,
                porcentaje: pct,
                desglose: g.latest!.desglose_reglas,
              })
            }
          >
            <Progress
              percent={Math.min(pct, 100)}
              size="small"
              strokeColor={pctColor(pct)}
              format={() => `${Math.round(pct)}%`}
              style={{ marginBottom: 0 }}
            />
          </div>
        );
      },
    },
    {
      title: 'Total Banco',
      key: 'total_banco',
      width: 130,
      align: 'center',
      render: (_, g) => (
        <Space direction="vertical" size={0}>
          <span>{g.latest ? formatMXN(g.latest.resumen.monto_total_banco) : '—'}</span>
          {g.latest?.resumen_por_moneda && Object.keys(g.latest.resumen_por_moneda).length > 1 && (
            <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>⚠ multi-moneda</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Diferencia',
      key: 'diferencia',
      width: 130,
      align: 'center',
      render: (_, g) => (
        <strong style={{ color: g.latest && g.latest.resumen.diferencia > 0 ? '#dc3545' : '#198754' }}>
          {g.latest ? formatMXN(g.latest.resumen.diferencia) : '—'}
        </strong>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 140,
      align: 'center',
      render: (_, g) => {
        const e = g.latest ? estadoLabel(g.latest.resumen.porcentaje_conciliado || 0) : null;
        return (
          <Space direction="vertical" size={2}>
            {e ? (
              <Tag style={{ background: e.bg, color: e.color, border: 'none', fontWeight: 700, margin: 0 }}>
                {e.text}
              </Tag>
            ) : (
              <Tag color="default" style={{ margin: 0 }}>Sin datos</Tag>
            )}
              {g.runs.length > 1 && (
              <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {g.runs.length} ejecuciones
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, g) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/reportes/${g.rfc}${g.latest?.id ? `?id=${g.latest.id}` : ''}`)}>
            Ver
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading === g.rfc}
            disabled={!g.latest}
            onClick={() => descargarExcel(g.rfc)}
            style={{ background: '#198754', borderColor: '#198754' }}
          >
            Excel
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) return <Loader tip="Cargando Reportes..." />;

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>Panel de Administración Global</Title>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Resumen de Empresas y Estado de Conciliación Fiscal
      </Paragraph>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={24} md={8}>
          <Card style={{ borderTop: '3px solid #2d6bc4' }}>
            <Statistic
              title={<span style={{ textAlign: 'center', display: 'block' }}>EMPRESAS ACTIVAS</span>}
              value={stats.empresasActivas}
              valueStyle={{ color: '#1a3a5c', textAlign: 'center', display: 'block' }}
            />
            <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>Usuarios Registrados</Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderTop: '3px solid #198754' }}>
            <Statistic
              title={<span style={{ textAlign: 'center', display: 'block' }}>EMPRESAS AL DÍA</span>}
              value={stats.empresasAlDia}
              suffix={stats.empresasActivas > 0 ? `/ ${stats.empresasActivas}` : ''}
              valueStyle={{ color: '#198754', textAlign: 'center', display: 'block' }}
            />
            <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>Con ≥80% conciliado</Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderTop: '3px solid #6f42c1' }}>
            <Statistic
              title={<span style={{ textAlign: 'center', display: 'block' }}>CFDIS CONCILIADOS</span>}
              value={stats.facturas.toLocaleString('es-MX')}
              valueStyle={{ color: '#1a3a5c', textAlign: 'center', display: 'block' }}
            />
            <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>Facturas Procesadas</Text>
          </Card>
        </Col>
      </Row>

      <Card title={<span style={{ textAlign: 'center', display: 'block' }}>Empresas Registradas</span>} size="small">
        <Table<RfcGroup>
          rowKey="rfc"
          columns={columns}
          dataSource={groups}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          expandable={{
            expandedRowRender: (g) => <ExpandedRfc g={g} onNavigate={(rfc, id) => navigate(`/reportes/${rfc}${id ? `?id=${id}` : ''}`)} />,
            rowExpandable: (g) => g.runs.length > 1 || !!(g.latest?.resumen_por_mes && g.latest.resumen_por_mes.length),
          }}
          size="middle"
          scroll={{ x: 1100 }}
        />
      </Card>

      <ReglasConciliacionModal
        visible={modalReglas.visible}
        onClose={() => setModalReglas(prev => ({ ...prev, visible: false }))}
        rfc={modalReglas.rfc}
        periodo={modalReglas.periodo}
        porcentaje={modalReglas.porcentaje}
        desglose={modalReglas.desglose}
      />
    </div>
  );
}

interface ExpandedProps {
  g: RfcGroup;
  onNavigate: (rfc: string, conciliacionId?: string) => void;
}

function ExpandedRfc({ g, onNavigate }: ExpandedProps) {
  const r = g.latest;
  if (!r) return null;
  const hasHistory  = g.runs.length > 1;
  const monthly     = r.resumen_por_mes;
  const hasMonthly  = !!monthly && monthly.length > 0;

  const historyColumns: ColumnsType<ReporteListItem> = [
    {
      title: 'Fecha',
      key: 'fecha',
      render: (_, run, idx) => (
        <span style={{ fontWeight: idx === 0 ? 600 : 400 }}>
          {run.fecha_ejecucion ? run.fecha_ejecucion.slice(0, 10) : '—'}
          {idx === 0 && <Tag color="blue" style={{ marginLeft: 6 }}>Última</Tag>}
        </span>
      ),
    },
    {
      title: 'Período',
      key: 'periodo',
      render: (_, run) =>
        run.periodo?.inicio
          ? `${run.periodo.inicio?.slice(0, 7)} → ${run.periodo.fin?.slice(0, 7)}`
          : '—',
    },
    {
      title: 'Conciliación',
      key: 'pct',
      render: (_, run) => {
        const pct = run.resumen.porcentaje_conciliado || 0;
        return (
          <Progress percent={Math.min(pct, 100)} size="small" strokeColor={pctColor(pct)} format={() => `${Math.round(pct)}%`} style={{ marginBottom: 0 }} />
        );
      },
    },
    { title: 'Conciliados',  key: 'matched',  align: 'center', render: (_, run) => <Text style={{ color: '#198754', fontWeight: 600, display: 'block', textAlign: 'center' }}>{run.resumen.matched}</Text> },
    { title: 'Sin CFDI', key: 'sin_cfdi', align: 'center', render: (_, run) => <Text style={{ color: '#664d03', display: 'block', textAlign: 'center' }}>{run.resumen.sin_cfdi}</Text> },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, run) => {
        const e = estadoLabel(run.resumen.porcentaje_conciliado || 0);
        return <Tag style={{ background: e.bg, color: e.color, border: 'none' }}>{e.text}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_, run) => (
        <Button size="small" type="link" onClick={() => onNavigate(run.rfc, run.id)}>
          Ver →
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 16px' }}>
      {hasHistory && (
        <div style={{ marginBottom: hasMonthly ? 24 : 0 }}>
          <Text strong style={{ fontSize: 12, color: '#6c757d' }}>
            Historial de Conciliaciones
          </Text>
          <Table<ReporteListItem>
            rowKey={(run, idx) => run.id || `${run.rfc}_${run.fecha_ejecucion}_${idx}`}
            columns={historyColumns}
            dataSource={g.runs}
            pagination={false}
            size="small"
            style={{ marginTop: 8 }}
          />
        </div>
      )}
      {hasMonthly && monthly && (
        <div>
          <Text strong style={{ fontSize: 12, color: '#6c757d' }}>
            Desglose mensual (última conciliación)
          </Text>
          <MonthlyTable mensual={monthly} totalPct={r.resumen.porcentaje_conciliado || 0} />
        </div>
      )}
    </div>
  );
}

interface MonthlyTableProps {
  mensual: ResumenMes[];
  totalPct: number;
}

function MonthlyTable({ mensual, totalPct }: MonthlyTableProps) {
  const denomMes = (m: ResumenMes) => m.matched + (m.matched_ia || 0) + m.sin_cfdi + m.sin_banco;

  const totals = useMemo(() => mensual.reduce((acc, m) => {
    acc.matched     += m.matched + (m.matched_ia || 0);
    acc.sin_cfdi    += m.sin_cfdi;
    acc.sin_banco   += m.sin_banco;
    acc.ppd_pagadas += m.ppd_pagadas || 0;
    acc.total_sat   += m.total_sat;
    acc.total_banco += m.total_banco;
    acc.denom       += denomMes(m);
    return acc;
  }, { matched: 0, sin_cfdi: 0, sin_banco: 0, ppd_pagadas: 0, total_sat: 0, total_banco: 0, denom: 0 }), [mensual]);

  const columns: ColumnsType<ResumenMes> = [
    { title: 'Mes', dataIndex: 'mes', key: 'mes', align: 'center', render: v => <strong style={{ color: '#1a3a5c' }}>{v}</strong> },
    { title: 'Conciliados',  key: 'matched',  align: 'center', render: (_, m) => <Text style={{ color: '#198754', fontWeight: 600, display: 'block', textAlign: 'center' }}>{m.matched + (m.matched_ia || 0)}</Text> },
    { title: 'Sin CFDI', key: 'sin_cfdi', align: 'center', render: (_, m) => <Text style={{ color: '#664d03', display: 'block', textAlign: 'center' }}>{m.sin_cfdi}</Text> },
    { title: 'Sin Banco', dataIndex: 'sin_banco', key: 'sin_banco', align: 'center', render: v => <Text style={{ color: '#7c3e0a' }}>{v}</Text> },
    { title: 'PPD pagadas', dataIndex: 'ppd_pagadas', key: 'ppd_pagadas', align: 'center', render: v => <Text style={{ color: '#0d6efd' }}>{v || 0}</Text> },
    { title: 'Total SAT',   dataIndex: 'total_sat',   key: 'total_sat',   align: 'center', render: v => formatMXN(v) },
    { title: 'Total Banco', dataIndex: 'total_banco', key: 'total_banco', align: 'center', render: v => formatMXN(v) },
    {
      title: '% Conciliado',
      key: 'pct',
      align: 'center',
      render: (_, m) => {
        const tot = denomMes(m);
        const pct = tot > 0 ? Math.round((m.matched + (m.matched_ia || 0)) / tot * 1000) / 10 : 0;
        return <strong style={{ color: pctColor(pct) }}>{pct}%</strong>;
      },
    },
  ];

  return (
    <Table<ResumenMes>
      rowKey="mes"
      columns={columns}
      dataSource={mensual}
      pagination={false}
      size="small"
      style={{ marginTop: 8 }}
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row style={{ background: '#f8f9fa', fontWeight: 700 }}>
            <Table.Summary.Cell index={0} align="center">TOTAL</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="center"><Text style={{ color: '#198754' }}>{totals.matched}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="center"><Text style={{ color: '#664d03' }}>{totals.sin_cfdi}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="center"><Text style={{ color: '#7c3e0a' }}>{totals.sin_banco}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="center"><Text style={{ color: '#0d6efd' }}>{totals.ppd_pagadas}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="center">{formatMXN(totals.total_sat)}</Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="center">{formatMXN(totals.total_banco)}</Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="center"><strong style={{ color: '#1a3a5c' }}>{totalPct}%</strong></Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
}
